// Logs out and redirects to /login after 30 minutes of no user activity.
// While the user is active, debounces /api/auth/check calls to slide the
// backend session forward without overwhelming the server.

import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

import { apiFetch } from "../api/client";

const INACTIVITY_MS = 30 * 60 * 1000;
const CHECK_DEBOUNCE_MS = 5 * 60 * 1000;
const ACTIVITY_EVENTS = ["click", "keydown", "pointermove", "touchstart"] as const;

export function useInactivityTimeout(enabled: boolean): void {
  const navigate = useNavigate();
  // Keep a ref so the stable closure below always has the latest navigate.
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  useEffect(() => {
    if (!enabled) return;

    let timerId: ReturnType<typeof setTimeout> | null = null;
    let lastCheck = Date.now();

    function doLogout() {
      if (timerId !== null) clearTimeout(timerId);
      apiFetch("/api/auth/logout", { method: "POST" }).catch(() => {});
      navigateRef.current("/login", { replace: true });
    }

    function onActivity() {
      if (timerId !== null) clearTimeout(timerId);
      timerId = setTimeout(doLogout, INACTIVITY_MS);

      const now = Date.now();
      if (now - lastCheck >= CHECK_DEBOUNCE_MS) {
        lastCheck = now;
        apiFetch("/api/auth/check")
          .then((r) => { if (!r.ok) doLogout(); })
          .catch(doLogout);
      }
    }

    // Start the inactivity countdown immediately.
    timerId = setTimeout(doLogout, INACTIVITY_MS);

    for (const ev of ACTIVITY_EVENTS) {
      document.addEventListener(ev, onActivity, { passive: true });
    }

    return () => {
      if (timerId !== null) clearTimeout(timerId);
      for (const ev of ACTIVITY_EVENTS) {
        document.removeEventListener(ev, onActivity);
      }
    };
  }, [enabled]);
}
